// ─────────────────────────────────────────────
//  Firebase Auth helpers
// ─────────────────────────────────────────────
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  updateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  createUserWithEmailAndPassword,
  getAuth,
} from "firebase/auth";
import { initializeApp, deleteApp } from "firebase/app";
import { auth, firebaseConfig } from "./config";
import { getUser, updateUser } from "./firestore";

// Sign in
export const login = async (email, password) => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  // Update last login timestamp
  await updateUser(result.user.uid, { lastLoginAt: new Date() });
  return result.user;
};

// Sign out
export const logout = () => signOut(auth);

// Password reset email
export const resetPassword = (email) =>
  sendPasswordResetEmail(auth, email);

// Listen to auth state changes — returns unsubscribe fn
export const onAuth = (callback) =>
  onAuthStateChanged(auth, callback);

// Get full user profile (Firebase Auth + Firestore role data)
export const getUserProfile = async (uid) => {
  const snap = await getUser(uid);
  if (!snap.exists()) return null;
  return { uid, ...snap.data() };
};

// Re-authenticate with the current password, then set a new one. Firebase
// requires a recent login for password changes, so the user must supply their
// current password — this also blocks anyone editing a left-open session.
export const changePassword = async (currentPassword, newPassword) => {
  const u = auth.currentUser;
  if (!u || !u.email) throw new Error("Not signed in.");
  const cred = EmailAuthProvider.credential(u.email, currentPassword);
  await reauthenticateWithCredential(u, cred);
  await updatePassword(u, newPassword);
};

// Re-authenticate, change the Auth email, then mirror it onto the Firestore
// profile so the rest of the app keeps showing one consistent address.
export const changeEmail = async (currentPassword, newEmail) => {
  const u = auth.currentUser;
  if (!u || !u.email) throw new Error("Not signed in.");
  const cred = EmailAuthProvider.credential(u.email, currentPassword);
  await reauthenticateWithCredential(u, cred);
  await updateEmail(u, newEmail);
  await updateUser(u.uid, { email: newEmail });
};

// Admin creates a new account WITHOUT disturbing their own session.
// createUserWithEmailAndPassword signs the new user into whatever Auth instance
// it runs on, so we run it on a throwaway secondary Firebase app and tear it
// down — the primary `auth` (the admin) is never touched. Returns the new uid.
export const createUserAccount = async (email, password) => {
  const secondary = initializeApp(firebaseConfig, `secondary-${Date.now()}`);
  try {
    const secondaryAuth = getAuth(secondary);
    const { user } = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    await signOut(secondaryAuth);
    return user.uid;
  } finally {
    await deleteApp(secondary);
  }
};

// Self sign-up. Unlike createUserAccount above, this runs on the PRIMARY auth
// instance because we want the new user signed in — that is the whole point.
// The caller is responsible for having set the sandbox flag first when the
// account is a sandbox one; otherwise AuthContext bootstraps a profile into
// the real database before a mode has been chosen.
export const signUp = async (email, password) => {
  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  return user;
};
